import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getAccountsCrmModules} from 'src/openapi/admin/apis/CrmModuleSettingsControllerApi';
import {useAppDispatch} from 'src/store/hooks';

import {selectSelectedColumns} from '../slices/account-search-result-columns.slice';
import {
    accountSearchResultCrmIntegrationActions,
    accountSearchResultCrmIntegrationDataSelector,
    accountSearchResultCrmIntegrationResultSelector,
} from '../slices/account-search-result-crm-integration.slice';
import {accountSearchResultStateSelector} from '../slices/account-search-result-state.slice';
import {checkShouldLoadColumn} from '../utils';

import type {AccountSearchResultCacheKeys} from '../typedef';
import type {AccountCrmModule} from 'src/openapi/admin/models/AccountCrmModule';


type Props = {
    accountIds: number[],
    totalCount: number,
    columnId: string,
};

export const useGetAccountSearchResultCrmIntegration = ({accountIds, totalCount, columnId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const selectedColumns = useSelector(selectSelectedColumns);

    const getAction = useCallback(() => apiDispatch(getAccountsCrmModules({
        requestBody: accountIds,
    })), [accountIds, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, AccountCrmModule>,
        dataCacheKeys: AccountSearchResultCacheKeys,
    ) => {
        dispatch(accountSearchResultCrmIntegrationActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    const shouldUpdateData = useMemo(
        () => checkShouldLoadColumn(columnId, selectedColumns),
        [columnId, selectedColumns],
    );

    return useCachedTable({
        dataSelector: accountSearchResultCrmIntegrationDataSelector,
        stateSelector: accountSearchResultStateSelector,
        resultSelector: accountSearchResultCrmIntegrationResultSelector,
        actions: accountSearchResultCrmIntegrationActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
        shouldUpdateData,
    });
};
